import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from '../../hooks/useInView';

interface ProductData {
  category: string;
  title: string;
  description: string;
  heroImage: string;
  benefits: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  products: Array<{
    name: string;
    description: string;
    image: string;
  }>;
  specifications: string[];
}

interface ProductTemplateProps {
  data: ProductData;
}

const ProductTemplate = ({ data }: ProductTemplateProps) => {
  const [heroRef, heroInView] = useInView({ threshold: 0.2 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.2 });
  const [productsRef, productsInView] = useInView({ threshold: 0.1 });
  const [specsRef, specsInView] = useInView({ threshold: 0.2 });

  return (
    <main>
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={data.heroImage}
            alt={data.category}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <div className="text-xs font-semibold text-primary-400 tracking-widest uppercase mb-4">
                {data.category}
              </div>
              <h1 className="font-display font-light text-5xl md:text-6xl leading-tight mb-6">
                {data.title}
              </h1>
              <p className="text-lg text-gray-200 leading-relaxed mb-8">
                {data.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-base font-medium rounded-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer"
                >
                  Request Pricing
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white text-base font-medium rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
              Business <span className="font-semibold">Benefits</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why leading contractors choose Go Global Inc. for their {data.category.toLowerCase()} needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {data.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl mx-auto mb-4">
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="font-display font-semibold text-lg text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Showcase */}
      <section ref={productsRef} className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={productsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
              Product <span className="font-semibold">Range</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive selection sourced from premium manufacturers worldwide
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={productsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.08, duration: 0.6 }}
                className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                    <button className="px-6 py-2 bg-white text-gray-900 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer">
                      Inquire
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications & Sourcing */}
      <section ref={specsRef} className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Specifications */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={specsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display font-semibold text-3xl text-gray-900 mb-6">
                Specifications & Options
              </h2>
              <ul className="space-y-3">
                {data.specifications.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-check-line text-primary-600 text-xl mr-3 mt-0.5"></i>
                    <span className="text-gray-700">{spec}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Global Sourcing Advantage */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={specsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
            >
              <h2 className="font-display font-semibold text-3xl mb-6">
                Global Sourcing Advantage
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-global-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Worldwide Network</h3>
                    <p className="text-white/90 text-sm">
                      Access to 5,000+ verified manufacturers across 40+ countries
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Quality Certified</h3>
                    <p className="text-white/90 text-sm">
                      All suppliers meet international quality and safety standards
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-price-tag-3-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Competitive Pricing</h3>
                    <p className="text-white/90 text-sm">
                      Direct factory pricing eliminates middlemen and reduces costs
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-6">
            Ready to <span className="font-semibold">Get Started?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Request pricing and learn how we can deliver {data.category.toLowerCase()} for your next project
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-base font-semibold rounded-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer"
          >
            Request Pricing
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ProductTemplate;